/**
 * RefusalSetCard — surfaces when the cascade engine flags 'refusal'
 * (transport_decision = 'patient refused' or unit_disposition implies AMA).
 *
 * Captures the capacity attestation: AOx?, risks explained, alternatives
 * offered, signature obtained. Critical for legal defensibility.
 */

import type { EPCR } from '@/types';
import { SetCard, LabeledRow, ChipSelect } from './SetCard';

interface Props {
  epcr: EPCR;
  onUpdate: (updates: Partial<EPCR>) => void;
}

type RefusalFindings = {
  capacity_aox?: 'A&Ox4' | 'A&Ox3' | 'A&Ox2' | 'A&Ox1' | 'altered';
  risks_explained?: string[];
  alternatives_offered?: string[];
  pt_repeat_back?: 'yes' | 'no' | 'partial';
  signature_obtained?: boolean;
  witness_present?: 'family' | 'LE' | 'bystander' | 'none';
};

const RISKS = ['death', 'permanent injury', 'worsening condition', 'inability to seek future care', 'unknown progression'];
const ALTERNATIVES = ['call 911 if condition worsens', 'follow up with PCP', 'go to ED by private vehicle', 'contact poison control', 'urgent care'];

export function RefusalSetCard({ epcr, onUpdate }: Props) {
  const f = ((epcr.set_findings ?? {}).refusal ?? {}) as RefusalFindings;
  const update = (patch: Partial<RefusalFindings>) => {
    onUpdate({
      set_findings: {
        ...(epcr.set_findings ?? {}),
        refusal: { ...f, ...patch },
      },
    });
  };
  const locked = epcr.locked;

  return (
    <SetCard
      title="Refusal / AMA Set"
      triggeredBy="Patient declined transport. Capacity attestation + signed refusal required for legal defensibility."
      feeds={['nemsis_xml', 'narrative_engine', 'audit_log']}
      accent="amber"
    >
      <LabeledRow label="Capacity (A&Ox)">
        <ChipSelect
          options={['A&Ox4', 'A&Ox3', 'A&Ox2', 'A&Ox1', 'altered']}
          value={f.capacity_aox ?? null}
          disabled={locked}
          onChange={(v) => update({ capacity_aox: v as RefusalFindings['capacity_aox'] })}
        />
      </LabeledRow>

      <LabeledRow label="Risks Explained" hint="Multi-select.">
        <ChipSelect
          options={RISKS}
          value={f.risks_explained ?? []}
          disabled={locked}
          multi
          onChange={(v) => update({ risks_explained: v as string[] })}
        />
      </LabeledRow>

      <LabeledRow label="Alternatives Offered">
        <ChipSelect
          options={ALTERNATIVES}
          value={f.alternatives_offered ?? []}
          disabled={locked}
          multi
          onChange={(v) => update({ alternatives_offered: v as string[] })}
        />
      </LabeledRow>

      <LabeledRow label="Pt able to repeat risks back?">
        <ChipSelect
          options={['yes', 'no', 'partial']}
          value={f.pt_repeat_back ?? null}
          disabled={locked}
          onChange={(v) => update({ pt_repeat_back: v as RefusalFindings['pt_repeat_back'] })}
        />
      </LabeledRow>

      <LabeledRow label="Witness present">
        <ChipSelect
          options={['family', 'LE', 'bystander', 'none']}
          value={f.witness_present ?? null}
          disabled={locked}
          onChange={(v) => update({ witness_present: v as RefusalFindings['witness_present'] })}
        />
      </LabeledRow>

      <LabeledRow label="Signature obtained?">
        <ChipSelect
          options={['yes', 'no']}
          value={f.signature_obtained === true ? 'yes' : f.signature_obtained === false ? 'no' : null}
          disabled={locked}
          onChange={(v) => update({ signature_obtained: v === 'yes' ? true : v === 'no' ? false : undefined })}
        />
      </LabeledRow>
    </SetCard>
  );
}
